/**
 * Live "this needs your attention" counts for sidebar menu items.
 *
 * WHAT A BADGE IS FOR
 * -------------------
 * A number beside a menu item is a nudge to go and DO something. That is the
 * whole test, and it is why there is no badge here for riders online or orders
 * delivered: a count that is healthy when it is large, or that is never zero,
 * trains people to stop reading every badge in the sidebar, including the ones
 * that matter. Every key below goes to zero when the work is done.
 *
 * HOW IT WORKS
 * ------------
 * `routes.js` names a key on a route (`liveBadge: "pendingSellers"`). A source
 * somewhere in the app publishes a count for that key with `setMenuBadge`. The
 * Sidebar subscribes and renders. Sources and menu items never reference each
 * other, so a badge can be moved between menu items, or fed from a different
 * source, without either side knowing.
 *
 * The label lives HERE rather than in routes.js, because the sentence a screen
 * reader announces is a property of what is being counted, not of where the
 * count happens to be shown. The original badge hardcoded "awaiting
 * verification" in the Sidebar, which was correct for exactly one key.
 */

/**
 * Every badge the app can show.
 *
 * `tone` maps to a CSS class and carries meaning, not decoration:
 *   attention - work waiting for a person (the default; amber)
 *   urgent    - something is blocked or losing money right now (red)
 *   info      - a queue that is normal to have, worth glancing at (teal)
 *
 * `label` receives the count and returns the full sentence used for the
 * tooltip and the screen-reader label.
 */
export const MENU_BADGES = Object.freeze({
  pendingSellers: {
    tone: "attention",
    label: (n) => `${n} seller${n === 1 ? "" : "s"} awaiting verification`,
  },
  adminProcessingOrders: {
    tone: "attention",
    label: (n) => `${n} order${n === 1 ? "" : "s"} waiting to be processed`,
  },
  inboxUnread: {
    tone: "attention",
    label: (n) => `${n} unanswered conversation${n === 1 ? "" : "s"}`,
  },
  brandOpenQuotes: {
    tone: "attention",
    label: (n) => `${n} open quote request${n === 1 ? "" : "s"}`,
  },
  partnerNewAssigned: {
    tone: "attention",
    label: (n) => `${n} order${n === 1 ? "" : "s"} awaiting a rider`,
  },
  ticketOrdersAttention: {
    // Urgent: each is a customer who paid and holds no ticket (none issued,
    // or the seats were lost and a refund is owed). Zero when every paid
    // ticket order has its tickets or its refund (16 Oct 2026).
    tone: "urgent",
    label: (n) => `${n} paid ticket order${n === 1 ? "" : "s"} without tickets or owed a refund`,
  },
  partnerFlaggedRiders: {
    // Urgent, and the one badge that is about money rather than work: each of
    // these riders is holding cash over their limit and has stopped being
    // offered orders. Capacity the partner is paying for and cannot dispatch.
    tone: "urgent",
    label: (n) =>
      `${n} rider${n === 1 ? "" : "s"} blocked by held cash and receiving no orders`,
  },
});

export const isKnownBadge = (key) => Object.hasOwn(MENU_BADGES, key);

// ── Store ────────────────────────────────────────────────────────────────────
// Deliberately the same shape as utilities/sellerReviewQueue.js: a module-level
// object plus a listener Set. The Sidebar renders on every route change, so a
// context would re-render the whole tree for a number that changes rarely.

let counts = {};
const listeners = new Set();

const notify = () => {
  for (const fn of listeners) {
    try {
      fn(counts);
    } catch (_e) {
      /* one bad subscriber must not stop the others */
    }
  }
};

/**
 * Publish a count. A source that has nothing to say should publish 0 rather
 * than skipping the call, so a badge disappears when its queue empties instead
 * of freezing at the last number anyone saw.
 */
export const setMenuBadge = (key, count) => {
  if (!isKnownBadge(key)) {
    // A typo in a source would otherwise be a badge that silently never shows.
    console.warn(`[menuBadges] unknown badge key "${key}"`);
    return;
  }
  const next = Number(count) || 0;
  if (counts[key] === next) return; // no-op writes must not re-render the menu
  counts = { ...counts, [key]: next };
  notify();
};

/** Clear everything. Call on sign-out so the next account starts clean. */
export const resetMenuBadges = () => {
  if (Object.keys(counts).length === 0) return;
  counts = {};
  notify();
};

export const getMenuBadges = () => counts;

export const subscribeMenuBadges = (fn) => {
  listeners.add(fn);
  // The immediate call needs the same guard as notify(): a subscriber that
  // throws on its first frame would otherwise take down whatever component was
  // mounting, and the Sidebar subscribes during render of every page.
  try {
    fn(counts);
  } catch (_e) {
    /* the subscription still stands; the next publish will try again */
  }
  return () => listeners.delete(fn);
};

/**
 * What the Sidebar needs to draw one badge, or null when there is nothing to
 * show. Zero is nothing to show: a badge reading 0 is a worse nudge than no
 * badge, because it takes up the same space and means the opposite.
 */
export const describeBadge = (key, allCounts = counts) => {
  const def = MENU_BADGES[key];
  if (!def) return null;
  const count = allCounts?.[key] || 0;
  if (count <= 0) return null;
  return {
    count,
    // Past 99 the exact number stops changing any decision, and a four figure
    // badge breaks the menu row.
    display: count > 99 ? "99+" : String(count),
    tone: def.tone,
    label: def.label(count),
  };
};
